import { MouseEvent, useEffect, useRef, useState } from "react";
import Button from "../Button/Button";
interface IProps {
    image: string;
    resetImage: () => void;
    setAvatar: (blob: Blob | null) => void;
    canvasWidth: number;
    canvasHeight: number;
}

const AvatarPicker = ({
    image,
    resetImage,
    setAvatar,
    canvasWidth,
    canvasHeight,
}: IProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [result, setResult] = useState<string | undefined>(undefined);
    if (typeof image == "object") {
        image = URL.createObjectURL(image);
    }
    const zoom = useRef(1);
    const canvasMouseX = useRef(0);
    const canvasMouseY = useRef(0);
    const dragCoordsX = useRef(0);
    const dragCoordsY = useRef(0);
    const offsetX = useRef(0);
    const offsetY = useRef(0);
    const canvasElement = useRef(null);
    const offsetCoordsX = useRef(0);
    const offsetCoordsY = useRef(0);
    const newImagePositionX = useRef(0);
    const newImagePositionY = useRef(0);

    const setCanvasMouse = (x: number, y: number) => {
        // gives coordinates relative to canvas element
        canvasMouseX.current = x - offsetX.current;
        canvasMouseY.current = y - offsetY.current;
    };
    const handleMouseDown = (e: MouseEvent) => {
        dragCoordsX.current = e.clientX - offsetX.current;
        dragCoordsY.current = e.clientY - offsetY.current;

        setIsDragging(true);
    };
    const handleMouseUp = (e: MouseEvent) => {
        let resX =
            offsetCoordsX.current + canvasMouseX.current - dragCoordsX.current;
        let resY =
            offsetCoordsY.current + canvasMouseY.current - dragCoordsY.current;
        offsetCoordsX.current = resX != 0 ? resX : offsetCoordsX.current;
        offsetCoordsY.current = resY != 0 ? resY : offsetCoordsY.current;
        setIsDragging(false);
    };
    const handleMouseOut = (e: MouseEvent) => {
        setCanvasMouse(e.clientX, e.clientY);
        setIsDragging(false);
    };

    const handeMouseMove = (e: MouseEvent) => {
        setCanvasMouse(e.clientX, e.clientY); // https://stackoverflow.com/questions/15036386/make-image-drawn-on-canvas-draggable-with-javascript
        if (isDragging) {
            (newImagePositionX.current =
                canvasMouseX.current -
                dragCoordsX.current +
                offsetCoordsX.current),
                (newImagePositionY.current =
                    canvasMouseY.current -
                    dragCoordsY.current +
                    offsetCoordsY.current),
                drawImage();
        }
    };
    const drawImage = () => {
        const img = new Image();
        //@ts-ignore
        const ctx = canvasElement!.current.getContext("2d");
        img.src = image;
        img.onload = () => {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.drawImage(
                img,
                newImagePositionX.current,
                newImagePositionY.current,
                img.width * zoom.current,
                img.height * zoom.current,
            );
        };
    };
    const useCanvas = () => {
        // https://stackoverflow.com/questions/15036386/make-image-drawn-on-canvas-draggable-with-javascript
        const img = new Image();
        //@ts-ignore
        const ctx = canvasElement!.current.getContext("2d");
        //@ts-ignore
        img.src = image;

        img.onload = () => {
            drawImage();
            //@ts-ignore
            const rect = canvasElement!.current.getBoundingClientRect();
            //@ts-ignore
            const win = canvasElement!.current.ownerDocument.defaultView;
            offsetX.current = rect!.left + win!.scrollX;
            offsetY.current = rect!.top + win!.scrollY;
        };
    };
    // Let user move image and zoom in or out.
    const handleZoomIn = () => {
        if (zoom.current < 2) {
            zoom.current = zoom.current + 0.25;
        }
        drawImage();
    };
    const handleZoomOut = () => {
        if (zoom.current > 0.25) {
            zoom.current = zoom.current - 0.25;
        }
        drawImage();
    };

    const handleConfirm = () => {
        //@ts-ignore
        const ctx = canvasElement!.current.getContext("2d");
        const img = new Image();
        drawImage();
        img.src = image;
        img.onload = () => {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.beginPath();
            ctx.arc(
                canvasWidth / 2,
                canvasHeight / 2,
                // Radius = half the canvans - 10% of that (since our reticle is 90%)
                canvasWidth / 2 - (canvasWidth / 2) * 0.1,
                0,
                2 * Math.PI,
            );
            // clip is permanent so we need to keep a clean
            // version of the canvas to restore it with .save()
            // and .restore()
            ctx.save();
            ctx.clip();
            ctx.drawImage(
                img,
                newImagePositionX.current,
                newImagePositionY.current,
                img.width * zoom.current,
                img.height * zoom.current,
            );
            // put image in another canvas, with the right dimensions
            const res = setTheRightDimensions();
            ctx.restore();
            drawImage();

            setResult(res);
        };
    };
    const setTheRightDimensions = () => {
        const canvas2 = document.createElement("canvas");
        const resWidth = canvasWidth - canvasWidth * 0.1;
        const resHeight = canvasHeight - canvasWidth * 0.1;
        canvas2.width = resWidth;
        canvas2.height = resHeight;
        const ctx2 = canvas2.getContext("2d");
        //@ts-ignore
        ctx2.drawImage(
            canvasElement.current!,
            (canvasWidth / 2) * 0.1,
            (canvasWidth / 2) * 0.1,
            canvasWidth,
            canvasHeight,
            0,
            0,
            canvasWidth,
            canvasHeight,
        );
        //@ts-ignore
        const result = canvas2.toDataURL();
        canvas2.toBlob(setAvatar);
        return result;
    };
    // On "save", clip the image present in the circle/ reticle, put it in another canvas and save.
    useEffect(() => {
        setTimeout(() => useCanvas(), 500);
    }, [zoom]);

    return (
        <div className="picker">
            {result == undefined ? (
                <>
                    <div className="graphical-items">
                        <div className="reticle"></div>
                        <canvas
                            id="canvas"
                            height={canvasHeight}
                            width={canvasWidth}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handeMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseOut={handleMouseOut}
                            ref={canvasElement}
                        ></canvas>
                        <div className="button-container__controls">
                            <Button preset="secondary" fn={handleZoomIn}>
                                +
                            </Button>
                            <Button preset="secondary" fn={handleZoomOut}>
                                -{" "}
                            </Button>
                        </div>
                    </div>
                    <div className="button-container__bottom">
                        <Button preset="primary" fn={handleConfirm}>
                            Confirm
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className="result__wrapper"
                        style={{ width: canvasWidth, height: canvasHeight }}
                    >
                        <img className="avatar" src={result} id="avatar" />
                    </div>
                    <div className="button-container__bottom">
                        <Button preset="secondary" fn={resetImage}>
                            Change image
                        </Button>
                        <Button
                            preset="secondary"
                            fn={(e) => {
                                e.preventDefault();
                                setResult(undefined);
                                setTimeout(() => {
                                    drawImage();
                                }, 100);
                            }}
                        >
                            Redo
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
export default AvatarPicker;
